import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { toTemplateSummary, type TenantTemplateSummary } from "../lib/contracts";

export interface UseTemplateSummaryResult {
  summary: TenantTemplateSummary | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch ``GET /api/tenant/templates`` and resolve the summary matching
 * ``selectedKey``. Returns ``null`` when no template is selected or the
 * catalog is unavailable. Rep/Viewer callers pass ``skip=true`` and the
 * hook never issues the request.
 */
export function useTenantTemplateSummary(
  selectedKey: string | null,
  skip = false,
): UseTemplateSummaryResult {
  const [summary, setSummary] = useState<TenantTemplateSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(!skip);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }
    if (!selectedKey) {
      setLoading(false);
      setSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get("/tenant/templates");
        const templates = Array.isArray(r?.data?.templates) ? r.data.templates : [];
        const raw = templates.find(
          (t: unknown) =>
            !!t && typeof t === "object" && (t as { key?: string }).key === selectedKey,
        );
        if (!cancelled) setSummary(raw ? toTemplateSummary(raw) : null);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (!cancelled) {
          if (status === 401) return;
          if (status === 403) setError("You do not have access to the template catalog.");
          else setError("The template catalog is temporarily unavailable.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedKey, skip]);

  return { summary, loading, error };
}
